import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import ConfidentialFolder from "@/models/People/ConfidentialFolder.js";

interface SectionAccess {
  classRead: boolean;
  classWrite: boolean;
  classMIS: boolean;
  state: string;
}

interface EnrolledSection {
  sectionId: string;
  additionalRoles?: string[];
  roleStatus: string;
}

interface ArchiveTarget {
  [key: string]: unknown;
}

interface RawConfidentialFolder {
  [key: string]: unknown;
}

export const useConfidentialFolderComposable = () => {
  const unregisteredPeopleList: Ref<ConfidentialFolder[]> = ref([]);
  const accessForPersonIdMap: Ref<Record<string, Record<string, SectionAccess>>> = ref({});

  /**
   * Reload unregistered people from backend
   */
  const reloadUnregisteredPeople = async () => {
    const response = await apiSkobo.get("/v2/appconfidentialfolder/obs/list");
    unregisteredPeopleList.value = response.data.map(
      (confidentialFolder: RawConfidentialFolder) => new ConfidentialFolder(confidentialFolder)
    );
  };

  /**
   * Load unregistered people if not already loaded
   */
  const loadUnregisteredPeople = async () => {
    if (!unregisteredPeopleList.value?.length) {
      await reloadUnregisteredPeople();
    }
  };

  /**
   * Archive a persons confidential documents - with a target back-end will move them to target.
   */
  const archiveConfidentialFolder = async (userId: string, target: ArchiveTarget) => {
    await apiSkobo.post(`/v2/appconfidentialfolder/obs/userfolders/${userId}/archive`, target);
    await reloadUnregisteredPeople();
  };

  const reloadAccessForPerson = async (personId: string) => {
    const response = await apiSkobo.get(`/AppConfidentialFolder/GetEnrolledUsers?personId=${personId}`);

    const accessMap: Record<string, SectionAccess> = {};

    response.data?.enrolledSections.forEach((es: EnrolledSection) => {
      const read = es.additionalRoles?.includes("ManuelLæs");
      const write = es.additionalRoles?.includes("ManuelSkriv");

      accessMap[es.sectionId] = {
        classRead: !!(read || write),
        classWrite: !!write,
        classMIS: !read && !write,
        state: es.roleStatus
      };
    });
    accessForPersonIdMap.value[personId] = accessMap;
  };

  const loadAccessForPerson = async (personId: string) => {
    if (!accessForPersonIdMap.value[personId]) {
      await reloadAccessForPerson(personId);
    }

    return accessForPersonIdMap.value[personId];
  };

  const addAccessForPerson = async (personId: string, sectionId: string, permission: string) => {
    await apiSkobo.post(
      `/AppConfidentialFolder/UpsertEnrolledUser?personId=${personId}&sectionId=${sectionId}&permission=${permission}`
    );

    await reloadAccessForPerson(personId);
  };

  const removeAccessForPerson = async (personId: string, sectionId: string) => {
    await apiSkobo.delete(`/AppConfidentialFolder/DeleteEnrolledUser?personId=${personId}&sectionId=${sectionId}`);

    await reloadAccessForPerson(personId);
  };

  // Getters
  const unregisteredPeople = computed(() => unregisteredPeopleList.value);

  const unRegisteredPersonById = computed(() => (personId: string) => {
    return unregisteredPeopleList.value.find(cf => cf.userId === personId);
  });

  const accessForPersonId = computed(() => (personId: string) => accessForPersonIdMap.value[personId]);

  return {
    unregisteredPeopleList,
    accessForPersonIdMap,
    loadUnregisteredPeople,
    reloadUnregisteredPeople,
    archiveConfidentialFolder,
    loadAccessForPerson,
    reloadAccessForPerson,
    addAccessForPerson,
    removeAccessForPerson,
    unregisteredPeople,
    unRegisteredPersonById,
    accessForPersonId
  };
};
